import { CompositeLayer, PickingInfo } from '@deck.gl/core/typed';
import chroma from 'chroma-js';
import rgba from 'color-rgba';
import Handlebars from 'handlebars';
import { getPresetColormap } from './colormap';
import { ModelData } from '../../schemas/world';
import { degrees_to_cardinal } from 'utils';

Handlebars.registerHelper('round', function (value, decimals) {
  return !isNaN(parseFloat(value)) ? value.toFixed(decimals) : '-';
});

Handlebars.registerHelper('multiply', function (value, scale) {
  return !isNaN(parseFloat(value)) ? scale * value : '-';
});

Handlebars.registerHelper('add', function (value, scale) {
  return !isNaN(parseFloat(value)) ? scale + value : '-';
});

Handlebars.registerHelper('degrees_to_cardinal', function (value) {
  return !isNaN(parseFloat(value)) ? degrees_to_cardinal(value) : '-';
});

export abstract class BaseWorldLayer {
  nodeType = 'WorldLayer';
  id: string;
  name: string;
  dataId: string | undefined;
  layerSpec: object;
  visible: boolean;
  showWith: string[] | null;
  linked: string;
  colormap: object;
  timeSelect: object;
  levelSelect: object;
  minZoom: number;
  maxZoom: number;
  _colormapper: object;

  constructor(spec) {
    this.id = spec.id;
    this.name = spec.name;
    this.dataId = spec.dataId;
    this.hoverInfo = spec.hoverInfo
      ? {
          ...spec.hoverInfo,
          template: Handlebars.compile(spec.hoverInfo.template),
        }
      : null;
    this.label = spec.label;
    this.panel = spec.panel;
    // null/undefined = no restrictions, [] = exclusive, [...ids] = allowed companions
    this.showWith = spec.showWith !== undefined ? spec.showWith : null;
    this.linked = spec.linked;
    this.visible = !!spec.visible;
    this.minZoom = spec.minZoom || 0;
    this.maxZoom = spec.maxZoom || 20;
    if (spec.layerSpec.colormap) {
      let colormap = spec.layerSpec.colormap;
      if (colormap && colormap.name) {
        colormap = getPresetColormap(colormap, 10);
      }
      if (typeof colormap.domain[0] == 'string') {
        if (colormap.domain.indexOf('_default') < 0)
          colormap.domain['_default'] = 'gray';
        this._colormapper = (v) => {
          const ind = colormap.domain.indexOf(v);
          const color =
            colormap.scale[
              ind >= 0 ? ind : colormap.domain.indexOf('_default')
            ];
          try {
            return chroma(color);
          } catch (e) {
            return chroma('black');
          }
        };
      } else {
        this._colormapper = chroma
          .scale(colormap.scale)
          .domain(colormap.domain);
      }
      this.colormap = colormap;
    }

    this.layerSpec = {
      ...spec.layerSpec,
      style: this.parseStyling(spec.layerSpec.style),
    };
    this.legend = this.getLegend();
    this.timeSelect = {
      mode: 'nearest',
      tolerance: 'PT60S',
      ...spec.timeSelect,
    };
    this.levelSelect = spec.levelSelect
      ? {
          mode: 'nearest',
          ...spec.levelSelect,
        }
      : null;
    this.pickable = true;
  }

  //Returns a Deck compatible composite layer for given data, time, level and context
  getDeckLayer(
    currentData: Promise[],
    time: Date[],
    viewstate: object,
    level: number | null,
  ): CompositeLayer<any> | null {
    return null;
  }
  //Returns a dynamic legend spec for the layer
  getLegend() {
    return null;
  }
  parseStyling(style) {
    if (!style) return {};
    const parsed_style = { ...style };
    Object.entries(style).map(([key, value]) => {
      if (key.slice(0, 3) == 'get') {
        if (value.function) {
          if (value.function == 'colormapper') {
            parsed_style[key] = (o) => {
              const _rgba = this._colormapper(
                (o.properties && o.properties[value.args[0]]) ||
                  o[value.args[0]],
              ).rgba();
              _rgba[3] = _rgba[3] * 255;
              return _rgba;
            };
          } else if (value.function[0] == '{' && value.args.length) {
            const fn = Function(...value.args, value.function);
            parsed_style[key] = (o) => {
              const args = value.args.map(
                (a) => (o.properties && o.properties[a]) || o[a],
              );
              return fn(...args);
            };
          }
        } else if (value.template) {
          const tmpl = Handlebars.compile(value.template);
          parsed_style[key] = (o) => {
            const result = tmpl(o.properties || o);
            if (key.slice(-5) == 'Color') {
              const [r, g, b, a] = rgba(result);
              return [r, g, b, a * 255];
            } else {
              return result;
            }
          };
        } else if (key.slice(-5) == 'Color' && typeof value == 'string') {
          const [r, g, b, a] = rgba(parsed_style[key]);
          parsed_style[key] = [r, g, b, a * 255];
        } else {
          parsed_style[key] = value;
        }
      }
    });
    return parsed_style;
  }
}
